import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db';
import { Loader2, FileText, Save, AlertCircle } from 'lucide-react';
import { logger } from '../../services/logger';

interface CompetitionTerms {
  competition_id: string;
  promo_name: string;
  terms_content: string | null;
  terms_version: number;
  terms_updated_at: string | null;
}

export function CompetitionTermsManager() {
  const [terms, setTerms] = useState<CompetitionTerms | null>(null);
  const [termsContent, setTermsContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentTerms();
  }, []);

  const fetchCurrentTerms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase.rpc('get_competition_terms', {
        p_competition_id: null // Get active competition
      });

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        const currentTerms = data[0] as CompetitionTerms;
        setTerms(currentTerms);
        setTermsContent(currentTerms.terms_content || '');
      }
    } catch (err) {
      logger.error('Error fetching competition terms:', err);
      setError('Failed to load competition terms');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!terms?.competition_id) {
      setError('No active competition found');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const { data, error: updateError } = await supabase.rpc('admin_update_competition_settings', {
        p_competition_id: terms.competition_id,
        p_competition_terms_content: termsContent
      });

      if (updateError) throw updateError;
      
      const result = data as { success: boolean; error?: string; message?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to update terms');
      }

      setSuccess('Competition terms and conditions updated successfully');
      await fetchCurrentTerms();
    } catch (err) {
      logger.error('Error updating competition terms:', err);
      setError(err instanceof Error ? err.message : 'Failed to update terms');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!terms) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md">
        <div className="flex items-center justify-center text-yellow-600 mb-4">
          <AlertCircle className="w-12 h-12" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-center">No Active Competition</h2>
        <p className="text-gray-600 text-center">
          There is no active competition configured. Please create a competition first before adding terms and conditions.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-purple-100 p-3 rounded-full">
          <FileText className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">Competition Terms & Conditions</h2>
          <p className="text-gray-500 text-sm">
            {terms.promo_name}
          </p>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md text-green-600">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
          <p className="text-sm text-purple-700">
            <strong>Note:</strong> These terms and conditions are specific to the current competition and will be displayed to users when they view the competition details. This is separate from the platform's general terms and conditions.
          </p>
        </div>

        <div>
          <label htmlFor="terms-content" className="block text-sm font-medium text-gray-700 mb-2">
            Terms & Conditions Content
          </label>
          <textarea
            id="terms-content"
            value={termsContent}
            onChange={(e) => setTermsContent(e.target.value)}
            rows={20}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
            placeholder={`Enter competition terms and conditions...

Example:

# ${terms.promo_name} Terms & Conditions

## Eligibility
1. Must be 18 years or older
2. Must have a verified account
3. Must accept these terms before participating

## Competition Rules
1. All participants start with R10,000 in virtual currency
2. The competition runs from [start date] to [end date]
3. Trading is limited to markets available on the platform

## Prizes
- 1st Place: [Prize description]
- 2nd Place: [Prize description]
- 3rd Place: [Prize description]

## Disqualification
Participants may be disqualified for:
- Violation of platform rules
- Multiple account usage
- Any form of market manipulation

## General
- Wayja reserves the right to modify these terms
- All decisions by Wayja are final`}
          />
          <p className="text-xs text-gray-500 mt-2">
            Supports plain text and basic formatting. Use clear headings and numbered lists for readability.
          </p>
        </div>

        {terms.terms_version > 0 && terms.terms_updated_at && (
          <div className="flex items-center justify-between text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            <span>Version: {terms.terms_version}</span>
            <span>Last updated: {new Date(terms.terms_updated_at).toLocaleString()}</span>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <button
            onClick={fetchCurrentTerms}
            disabled={isSaving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

